import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Upload, FileCheck, FileX, ArrowRight, ShieldCheck } from "lucide-react";

const HOUSING_TYPES = ["transitional", "permanent_supportive", "sober_living", "veteran_housing", "shelter", "halfway_house", "treatment", "other"];
const POPULATIONS = ["veterans", "recovery", "reentry", "youth", "women", "men", "families", "seniors", "lgbtq"];
const REQUIRED_DOCS = [
  { type: "proof_of_organization", label: "Proof of Organization / Business" },
  { type: "licensing", label: "Licensing or Certification" },
  { type: "insurance", label: "Insurance Documentation" },
];
const OPTIONAL_DOCS = [
  { type: "photos", label: "Facility Photos" },
  { type: "brochure", label: "Program Brochure" },
  { type: "other", label: "Other Verification Documents" },
];

export default function ProviderApplication() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/provider/application/current");
    setApp(data);
    setFields(data.fields || {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user || !["provider", "agency_owner", "agency_staff", "case_manager", "referral_partner_pro"].includes(user.role)) {
    return <div className="max-w-2xl mx-auto p-6 text-center">A Provider, Agency, Case Manager, or Referral Professional account is required to access this page.</div>;
  }

  const locked = app && !["draft", "denied"].includes(app.status);

  const updateField = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const togglePopulation = (p) => {
    const list = fields.populations_served || [];
    setFields({ ...fields, populations_served: list.includes(p) ? list.filter((x) => x !== p) : [...list, p] });
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put("/provider/application/current", fields);
      toast.success("Draft saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadDoc = async (file, type) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("name", file.name);
    form.append("document_type", type);
    form.append("notes", `Provider application: ${type}`);
    try {
      const { data: doc } = await api.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/provider/application/current/attach-document", { document_id: doc.id, type });
      toast.success(`Uploaded: ${file.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const removeDoc = async (document_id) => {
    try {
      await api.delete(`/provider/application/current/document/${document_id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/provider/application/current/submit");
      toast.success("Application submitted for review");
      await refresh();
      navigate("/dashboard?onboarding=complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (!app) return <div className="max-w-3xl mx-auto p-6 text-center">Loading application…</div>;

  const docByType = {};
  (app.documents_hydrated || []).forEach((d) => { docByType[d.type] = d; });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="provider-application-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0A192F]">Provider Application</h1>
          <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">Complete this application to be reviewed and approved as a verified housing provider on Safe Harbor Veterans.</p>
        </div>
        <Badge data-testid="application-status-pill" className={
          app.status === "approved" ? "bg-[#C6F6D5] text-[#22543D]" :
          app.status === "denied" ? "bg-[#FED7D7] text-[#742A2A]" :
          app.status === "submitted" ? "bg-[#BEE3F8] text-[#2C5282]" : "bg-[#FEF6E4] text-[#744210]"
        }>Status: {app.status.replace(/_/g, ' ')}</Badge>
      </div>

      {app.status === "denied" && app.denial_reason && (
        <div className="bg-[#FED7D7] border border-[#FC8181] rounded-sm p-4 mb-6">
          <div className="font-semibold text-[#742A2A]">Previously denied — reason:</div>
          <div className="text-sm text-[#742A2A] mt-1">{app.denial_reason}</div>
          <div className="text-xs text-[#742A2A] mt-2">Update the application and resubmit. Your new submission will be tracked as version {(app.version || 1) + 1}.</div>
        </div>
      )}

      {locked && (
        <div className="bg-[#BEE3F8] border border-[#2C5282] rounded-sm p-4 mb-6 text-sm text-[#2C5282]">
          Your application is currently under admin review. You will be notified by email when a decision is made.
        </div>
      )}

      {/* Section 1: Organization Info */}
      <Section title="Organization">
        <Grid>
          <Field label="Facility / Organization Name *" testId="field-facility-name">
            <Input data-testid="input-facility-name" value={fields.facility_name || ""} onChange={(e) => updateField("facility_name", e.target.value)} disabled={locked} />
          </Field>
          <Field label="Contact Person *" testId="field-contact-person">
            <Input data-testid="input-contact-person" value={fields.contact_person || ""} onChange={(e) => updateField("contact_person", e.target.value)} disabled={locked} />
          </Field>
          <Field label="Email *" testId="field-email">
            <Input data-testid="input-email" type="email" value={fields.email || ""} onChange={(e) => updateField("email", e.target.value)} disabled={locked} />
          </Field>
          <Field label="Phone *" testId="field-phone">
            <Input data-testid="input-phone" value={fields.phone || ""} onChange={(e) => updateField("phone", e.target.value)} disabled={locked} />
          </Field>
          <Field label="Website" testId="field-website">
            <Input data-testid="input-website" placeholder="https://" value={fields.website || ""} onChange={(e) => updateField("website", e.target.value)} disabled={locked} />
          </Field>
          <Field label="Capacity (Bed Count) *" testId="field-capacity">
            <Input data-testid="input-capacity" type="number" min="1" value={fields.capacity_beds || ""} onChange={(e) => updateField("capacity_beds", parseInt(e.target.value || "0") || null)} disabled={locked} />
          </Field>
        </Grid>
      </Section>

      <Section title="Location">
        <Grid>
          <Field label="Street Address *" full testId="field-address">
            <Input data-testid="input-address" value={fields.address || ""} onChange={(e) => updateField("address", e.target.value)} disabled={locked} />
          </Field>
          <Field label="City *" testId="field-city">
            <Input data-testid="input-city" value={fields.city || ""} onChange={(e) => updateField("city", e.target.value)} disabled={locked} />
          </Field>
          <Field label="State *" testId="field-state">
            <Input data-testid="input-state" maxLength="2" placeholder="NC" value={fields.state || ""} onChange={(e) => updateField("state", e.target.value.toUpperCase())} disabled={locked} />
          </Field>
          <Field label="ZIP" testId="field-zip">
            <Input data-testid="input-zip" value={fields.zip_code || ""} onChange={(e) => updateField("zip_code", e.target.value)} disabled={locked} />
          </Field>
        </Grid>
      </Section>

      <Section title="Services">
        <Grid>
          <Field label="Housing Type *" testId="field-housing-type">
            <select data-testid="input-housing-type" value={fields.housing_type || ""} onChange={(e) => updateField("housing_type", e.target.value)} disabled={locked} className="border border-[#D1D5DB] rounded-sm px-3 py-2 text-sm w-full">
              <option value="">Select…</option>
              {HOUSING_TYPES.map((h) => <option key={h} value={h}>{h.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
          <Field label="Veteran-Specific Services" testId="field-veteran-services" full>
            <Textarea data-testid="input-veteran-services" rows={3} value={fields.veteran_specific_services || ""} onChange={(e) => updateField("veteran_specific_services", e.target.value)} disabled={locked} placeholder="VA partnerships, peer support, employment programs, etc." />
          </Field>
          <Field label="Populations Served" testId="field-populations" full>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
              {POPULATIONS.map((p) => (
                <label key={p} className="flex items-center gap-2 text-sm" data-testid={`pop-${p}`}>
                  <Checkbox checked={(fields.populations_served || []).includes(p)} onCheckedChange={() => togglePopulation(p)} disabled={locked} />
                  <span className="capitalize">{p}</span>
                </label>
              ))}
            </div>
          </Field>
        </Grid>
      </Section>

      <Section title="Credentials">
        <Grid>
          <Field label="Licensing / Certifications" full testId="field-licensing">
            <Textarea data-testid="input-licensing" rows={2} value={fields.licensing_certifications || ""} onChange={(e) => updateField("licensing_certifications", e.target.value)} disabled={locked} />
          </Field>
          <Field label="Insurance Information" full testId="field-insurance">
            <Textarea data-testid="input-insurance" rows={2} value={fields.insurance_information || ""} onChange={(e) => updateField("insurance_information", e.target.value)} disabled={locked} />
          </Field>
        </Grid>
      </Section>

      <Section title="Documents" required>
        <div className="text-xs text-[#4A5568] mb-3">Required documents must be uploaded before submitting for review.</div>
        <div className="space-y-3">
          {REQUIRED_DOCS.map((d) => (
            <DocRow key={d.type} doc={docByType[d.type]} label={d.label} type={d.type} required onUpload={(f) => uploadDoc(f, d.type)} onRemove={removeDoc} disabled={locked} />
          ))}
          <div className="text-xs uppercase tracking-widest text-[#4A5568] mt-6 mb-2">Optional</div>
          {OPTIONAL_DOCS.map((d) => (
            <DocRow key={d.type} doc={docByType[d.type]} label={d.label} type={d.type} onUpload={(f) => uploadDoc(f, d.type)} onRemove={removeDoc} disabled={locked} />
          ))}
        </div>
      </Section>

      {!locked && (
        <div className="flex items-center gap-3 flex-wrap mt-8 pt-6 border-t border-[#D1D5DB]">
          <Button data-testid="save-draft-btn" variant="outline" onClick={saveDraft} disabled={saving}>{saving ? "Saving…" : "Save Draft"}</Button>
          <Button data-testid="submit-application-btn" onClick={submit} disabled={submitting} className="bg-[#1E3A8A] hover:bg-[#1A365D]">
            {submitting ? "Submitting…" : (<>Submit for Review <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
          <div className="text-xs text-[#4A5568] ml-auto">Version {app.version} · Last saved {(app.updated_at || "").slice(0, 16).replace("T", " ")}</div>
        </div>
      )}

      {app.stage_history && app.stage_history.length > 0 && (
        <div className="mt-10 pt-6 border-t border-[#D1D5DB]">
          <div className="font-heading text-sm font-semibold text-[#0A192F] mb-3">Application History</div>
          <ul className="text-xs space-y-1 text-[#4A5568]">
            {app.stage_history.map((h, i) => (
              <li key={`${h.ts || ""}-${h.stage || ""}-${i}`}><span className="capitalize font-medium text-[#0A192F]">{h.stage}</span> · {(h.ts || "").slice(0, 16).replace("T", " ")} · {h.notes}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, required, children }) => (
  <div className="bg-white border border-[#D1D5DB] rounded-sm p-5 mb-4">
    <div className="font-heading text-base font-semibold text-[#0A192F] mb-3">{title}{required && <span className="text-[#C05621] ml-1">*</span>}</div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children, full, testId }) => (
  <div className={full ? "sm:col-span-2" : ""} data-testid={testId}>
    <Label className="text-xs uppercase tracking-widest text-[#4A5568]">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
const DocRow = ({ doc, label, type, required, onUpload, onRemove, disabled }) => (
  <div className="flex items-center gap-3 border border-[#D1D5DB] rounded-sm p-3" data-testid={`doc-row-${type}`}>
    {doc?.document ? <FileCheck className="w-5 h-5 text-[#276749]" /> : <FileX className="w-5 h-5 text-[#4A5568]" />}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-[#0A192F]">{label}{required && <span className="text-[#C05621] ml-1">required</span>}</div>
      {doc?.document ? (
        <div className="text-xs text-[#4A5568] truncate">{doc.document.name} · {(doc.uploaded_at || "").slice(0, 10)}</div>
      ) : (
        <div className="text-xs text-[#4A5568]">No file uploaded yet.</div>
      )}
    </div>
    {doc?.document ? (
      !disabled && <Button data-testid={`remove-doc-${type}`} variant="outline" size="sm" onClick={() => onRemove(doc.document.id)}>Remove</Button>
    ) : (
      !disabled && (
        <label className="cursor-pointer">
          <input data-testid={`upload-doc-${type}`} type="file" className="hidden" onChange={(e) => onUpload(e.target.files?.[0], type)} />
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white"><Upload className="w-3.5 h-3.5" /> Upload</span>
        </label>
      )
    )}
  </div>
);
